import React, { useState } from 'react';
import '../styles/CommissionModal.css';

const CommissionModal = ({ isOpen, onClose, gpPerHour }) => {
  const [commissionType, setCommissionType] = useState('consulting');
  const [netFees, setNetFees] = useState('');
  const [placementType, setPlacementType] = useState('both');
  
  if (!isOpen) return null;

  // Consulting commission tiers
  const consultingTiers = [
    { from: 35.0, to: Infinity, rate: 12 },
    { from: 20.0, to: 34.99, rate: 10 },
    { from: 15.0, to: 19.99, rate: 8 },
    { from: 10.0, to: 14.99, rate: 6 },
    { from: 0.0, to: 9.99, rate: 3 }
  ];

  // Calculate consulting commission based on net margin per hour
  const calculateConsultingCommission = () => {
    if (gpPerHour <= 0) return 0;
    
    const tier = consultingTiers.find(t => gpPerHour >= t.from && gpPerHour <= t.to);
    if (!tier) return 0;
    
    return (gpPerHour * tier.rate) / 100;
  };

  // Calculate permanent placement commission
  const calculatePermanentCommission = () => {
    const fees = parseFloat(netFees) || 0;
    if (fees <= 0) return 0;

    switch (placementType) {
      case 'sales':
        return fees * 0.1; // 10% for sales only
      case 'recruitment':
        return fees * 0.1; // 10% for recruitment only
      case 'both':
        return fees * 0.2; // 20% for both
      default:
        return 0;
    }
  };

  const getConsultingTier = () => {
    if (gpPerHour <= 0) return null;
    return consultingTiers.find(t => gpPerHour >= t.from && gpPerHour <= t.to);
  };

  const consultingCommission = calculateConsultingCommission();
  const permanentCommission = calculatePermanentCommission();
  const currentTier = getConsultingTier();

  return (
    <div className="commission-modal-overlay" onClick={onClose}>
      <div className="commission-modal-content" onClick={e => e.stopPropagation()}>
        <div className="commission-modal-header">
          <h2>Commission Calculator</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="commission-modal-body">
          {/* Type Selector Tabs */}
          <div className="tabs-container">
            <button
              className={`tab-button ${commissionType === 'consulting' ? 'active' : ''}`}
              onClick={() => setCommissionType('consulting')}
            >
              Consulting Position
            </button>
            <button
              className={`tab-button ${commissionType === 'permanent' ? 'active' : ''}`}
              onClick={() => setCommissionType('permanent')}
            >
              Permanent Placement
            </button>
          </div>

          {/* Consulting Commission Section */}
          {commissionType === 'consulting' && (
            <div className="commission-section">
              <h3>Consulting Commission</h3>
              <p className="commission-description">
                Commission is based on net margin dollars per hour
              </p>
              
              <div className="commission-inputs">
                <div className="input-group">
                  <label>Net Margin Per Hour</label>
                  <div className="input-value">${gpPerHour.toFixed(2)}</div>
                </div>
              </div>

              {currentTier ? (
                <div className="commission-tier-info">
                  <p><strong>Applicable Tier:</strong> ${currentTier.from.toFixed(2)} - ${currentTier.to === Infinity ? '$35+' : '$' + currentTier.to.toFixed(2)}</p>
                  <p><strong>Commission Rate:</strong> {currentTier.rate}%</p>
                </div>
              ) : (
                <div className="commission-tier-info">
                  <p>No applicable tier (net margin must be $0 or above)</p>
                </div>
              )}

              <div className="commission-result">
                <p className="result-label">Estimated Monthly Commission</p>
                <p className="result-value">${(consultingCommission * 160).toFixed(2)}</p>
                <p className="result-note">Based on 160 hours worked per month</p>
                <p className="result-monthly">
                  <strong>Estimated Commission Per Hour:</strong> ${consultingCommission.toFixed(2)}
                </p>
              </div>

              <div className="commission-scale">
                <h4>Commission Schedule</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Net Margin Per Hour</th>
                      <th>Commission %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>$35.00 & above</td>
                      <td>12%</td>
                    </tr>
                    <tr>
                      <td>$20.00 - $34.99</td>
                      <td>10%</td>
                    </tr>
                    <tr>
                      <td>$15.00 - $19.99</td>
                      <td>8%</td>
                    </tr>
                    <tr>
                      <td>$10.00 - $14.99</td>
                      <td>6%</td>
                    </tr>
                    <tr>
                      <td>$0.00 - $9.99</td>
                      <td>3%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Permanent Placement Commission Section */}
          {commissionType === 'permanent' && (
            <div className="commission-section">
              <h3>Permanent Placement Commission</h3>
              <p className="commission-description">
                Commission based on net fees and placement type
              </p>

              <div className="commission-inputs">
                <div className="input-group">
                  <label htmlFor="netFees">Net Fees ($)</label>
                  <input
                    type="number"
                    id="netFees"
                    value={netFees}
                    onChange={(e) => setNetFees(e.target.value)}
                    placeholder="Enter net fees"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="placementType">Placement Type</label>
                  <select
                    id="placementType"
                    value={placementType}
                    onChange={(e) => setPlacementType(e.target.value)}
                  >
                    <option value="sales">Sales Only (10%)</option>
                    <option value="recruitment">Recruitment Only (10%)</option>
                    <option value="both">Sales & Recruitment (20%)</option>
                  </select>
                </div>
              </div>

              <div className="commission-result">
                <p className="result-label">Commission Amount</p>
                <p className="result-value">${permanentCommission.toFixed(2)}</p>
              </div>

              <div className="commission-scale">
                <h4>Commission Schedule</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Commission %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Sales Only</td>
                      <td>10% of Net Fees</td>
                    </tr>
                    <tr>
                      <td>Recruitment Only</td>
                      <td>10% of Net Fees</td>
                    </tr>
                    <tr>
                      <td>Sales & Recruitment</td>
                      <td>20% of Net Fees</td>
                    </tr>
                  </tbody>
                </table>
                <p className="definition">
                  <strong>Net Fees:</strong> Fees earned minus any referral fees payable or other costs attributable to recruitment and sales.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionModal;
